import { CODEX_ALL } from '../data/codex-list';
import { camera } from '../render/scene';
import {
  aimCodexLights, enterCodexScene, exitCodexScene, setCodexAccent, tickCodexScene
} from '../render/codex-scene';
import {
  modelMeshes, setCodexDie, setCodexModel, setCodexModelVisible, tickCodexModel
} from '../render/codex-model';
import { FIT, place, placeCodexCamera } from '../render/codex-fit';
import { clearCodexSwarm, setCodexSwarm } from '../render/codex-swarm';
import { bindDevControls, devControlsHTML } from './codex-dev';
import {
  VIEW, bindCodexView, codexZoom, pokeCodexView, resetCodexView, tickCodexView
} from './codex-view';
import { TINT } from '../entities/enemy-parts';
import {
  codexProgress, initCodexList, renderList, resetCodexFilters, visibleList
} from './codex-list';
import { renderData } from './codex-data';
import { renderStageStrip, syncFormButtons } from './codex-forms';
import {
  LAY, applyCodexLayout, resetCodexLayout, toggleCodexData, toggleCodexList
} from './codex-layout';
import { codexStage, eliteKey } from '../platform/storage';
import { damp } from '../core/math';
import { $ } from '../platform/dom';
import { openPause } from './screens/pause';
/* ============================================================
   圖鑑：開關、輸入、選中狀態、開發模式
   ============================================================

   **一套程式碼、兩個模式**（使用者 2026-08-02 定案）：
   玩家模式（情報庫）與開發模式（驗收台）共用渲染與資料，
   差別只在「顯示什麼」—— 不是兩個畫面，所以版面不會跳。

   開發模式四件（使用者全選）：
     ① 變體／精英切換　② 群體壓力 0–420 + draw call 讀數
     ③ 剪影模式（全黑）　④ 忽略解鎖（看得到全部）

   入口：開始畫面按鈕 + 對局中暫停（`game.ts` 接線）。
   **不做 console 指令** —— 使用者沒選。

   鏡頭與機庫同一套做法：`VIEW` 是當前值、`T` 是目標值，damp 追上去；
   視錐偏移去量 DOM 的實際矩形（版面與鏡頭各寫一份常數遲早會對不上）。

   ⚠️ **旋轉一律由鏡頭負責，展品不自己轉**（B3 修掉的缺陷）。
   舊版兩邊都轉同一個 yaw，完全抵消 —— 拖曳只把房間轉走，展品永遠正面
   對著鏡頭。四顆快捷視角因此才有意義：它們設的是鏡頭的 yaw/pitch。 */

/* `$` 來自 `platform/dom`：它在 headless（`initDomHeadless`）下回 null。
   **不要直接 `document.getElementById`** —— `game.ts` 的主迴圈每幀都會問一次
   `codexOpen()`，直接碰 `document` 會讓 Node 測試整支炸掉（`flow.test.ts`
   就是這樣紅的）。同理 `syncCodexView()` 裡的 `document.querySelector` 也要擋。 */

var selId = CODEX_ALL.length ? CODEX_ALL[0].id : null;
var dev = false;
var st = { elite: false, vi: 0, sil: false, swarm: 0, stg: 0, die: false };

/** **查得到全部 63 種**（含開發模式才列出的 19 隻 Boss）——
    切掉開發模式時選中的那一隻不該憑空消失，只是不列在清單上而已。 */
function typeById(id) {
  for (var i = 0; i < CODEX_ALL.length; i++) if (CODEX_ALL[i].id === id) return CODEX_ALL[i];
  return null;
}

/** 這一格現在該看哪一個解鎖狀態。**精英是獨立條目**（使用者 2026-08-03 指定）：
    切到精英就讀精英那一份 `seen`/`killed`，一樣走「看過→剪影、打死→完整」。
    Boss 沒有精英形式，一律讀自己那一份。 */
function curStage() {
  if (dev) return 2;
  var T = typeById(selId);
  return codexStage(st.elite && T && !T.boss ? eliteKey(selId) : selId);
}

/** 這一隻的精英形式解鎖到哪 —— 決定「精英」那顆鈕是不是鎖著的。 */
function eliteStage() {
  var T = typeById(selId);
  if (!T || T.boss) return 0;
  return dev ? 2 : codexStage(eliteKey(selId));
}

/** 展示台上該擺什麼。**解鎖階段決定的是材質不是內容** ——
    第二段（看過沒打死）用同一份 part 表與同一份動畫，只換成不受光的黑，
    所以玩家看得到牠怎麼動、看不出牠是誰。 */
function refreshModel() {
  var T = typeById(selId);
  var stage = curStage();
  if (!T || stage === 0) { setCodexModel(null, {}); return; }
  setCodexModel(T, {
    /* 精英現在**玩家模式也切得動**（解鎖了才切得動）。 */
    elite: st.elite && !T.boss, vi: dev ? st.vi : 0,
    sil: (dev && st.sil) || stage === 1,
    /* 階段只有開發模式切得動。玩家模式一律看 P1 —— 二階段是**打出來的**，
       在圖鑑裡先看到等於把那個轉折先講掉了。 */
    stg: dev ? Math.min(st.stg, (T.stages || 2) - 1) : 0
  });
  setCodexDie(dev && st.die);
}

export function renderCodex() {
  var T = typeById(selId), stage = curStage();
  syncFormButtons(T, st.elite, eliteStage());
  renderStageStrip(T, stage, dev, st.stg, function (i) {
    st.stg = i; renderCodex(); refreshModel();
  });
  renderList(selId, dev, function (id) { select(id); });
  renderData(T, dev, st.elite, stage);
  $('cProg').textContent = codexProgress();
  /* 掃描艙的大標題**只在檔案欄看不到的時候才出現**（窄視窗的抽屜模式）。
     兩邊都顯示就是同一個名字印兩次 —— 實看四種尺寸量出來的毛病之一。 */
  $('cTitle').innerHTML = !T ? '—'
    : (stage === 0 ? '？？？？' : (stage === 1 ? '█████' : T.code));
  $('cSub').textContent = !T ? '' : (stage === 2 ? T.role : (stage === 1 ? '身分未確認' : '未登錄'));
  $('cDev').innerHTML = dev ? devControlsHTML(T, st) : '';
  if (dev) bindDevControls(T, st, refreshModel, function () { renderCodex(); refreshModel(); });
  $('btnCodexDev').classList.toggle('on', dev);
  $('cBadge').textContent = '';
  /* 背景的威脅色只在第三段給 —— 未解鎖的展品配一片中性背景，
     跟右欄「威脅未知」是同一條規則。 */
  var C = stage === 2 && T ? (TINT[T.tint] || TINT.red) : null;
  setCodexAccent(C ? C.hex : 0);
}

export function setCodexForm(elite) {
  var T = typeById(selId);
  if (T && T.boss) return;
  if (elite && eliteStage() === 0) return;      // 還沒遇過精英，那顆是鎖的
  st.elite = !!elite;
  renderCodex();
  refreshModel();
}


export function select(id) {
  if (!typeById(id)) return;
  selId = id;
  st.swarm = 0;
  st.elite = false;             // 換一隻就回到一般型（新的那隻精英不一定解鎖）
  renderCodex();
  refreshModel();
  pokeCodexView();
}

/** 上下鍵換敵種。**跨家族連續** —— 圖鑑是一本書不是八本。
    走的是「**現在畫面上列得出來**的那一份」（含搜尋與快篩），
    否則會跳到一個清單上根本看不到的條目。
    選中的那一隻被篩掉時從頭走起。 */
function step(d) {
  var list = visibleList(dev);
  if (!list.length) return;
  var i = -1;
  for (var k = 0; k < list.length; k++) if (list[k].id === selId) i = k;
  select(i < 0 ? list[d > 0 ? 0 : list.length - 1].id
    : list[(i + d + list.length) % list.length].id);
}

/* ---------- 版面 → 鏡頭 ---------- */

var ZERO = { width: 0, height: 0, left: 0, top: 0 };
function box(id) {
  var el = $(id);
  return el ? el.getBoundingClientRect() : ZERO;
}
/** 視錐偏移：相機仍然繞著展品轉，只把投影中心挪到中欄。
    **量的是實際 DOM**，所以 CSS 改欄寬、換窄視窗都自動跟著對
    （機庫那一輪的結論，`ui/hangar.ts` 有完整說明）。 */
export function syncCodexView() {
  if (!codexOpen()) return;
  if (typeof document === 'undefined') return;
  var cv = document.querySelector('#stage canvas') as any;
  var w = cv ? cv.clientWidth : window.innerWidth;
  var h = cv ? cv.clientHeight : window.innerHeight;
  var list = box('cList'), foot = box('cFoot'), stage = box('cStage');
  /* 抽屜模式的檔案欄是**覆蓋層**，不佔版面 —— 它的矩形不可以拿去做視錐偏移，
     否則一拉開抽屜展品就整個往旁邊跳。收起來時 `display:none` 本來就量到 0，
     所以只有「拉開」這個狀態需要特別處理。 */
  var data = LAY.drawer ? ZERO : box('cData');
  camera.setViewOffset(w, h, (data.width - list.width) / 2, foot.height / 2, w, h);
  /* **掃描艙比畫布小多少，鏡頭就退多遠**（垂直與水平各補各的）。
     不補的話展品會以整張畫布構圖，上下被底列切掉、右邊的比例尺機甲
     被檔案欄蓋掉半個身體。量的還是實際 DOM，所以改欄寬不用改常數。 */
  var bayH = Math.max(80, stage.height - foot.height);
  var bayW = Math.max(80, stage.width);
  FIT.k = Math.max(1, h / bayH);
  FIT.kx = Math.max(1, w / bayW);
  place();
}

/* ---------- 每個畫面幀 ---------- */

export function updateCodex(dt, t) {
  if (!codexOpen()) return;
  tickCodexView(dt);
  tickCodexScene(t);
  tickCodexModel(t);

  var T = typeById(selId);
  var swarm = dev && st.swarm > 0 && T && !T.boss;
  setCodexModelVisible(!swarm);
  var s = setCodexSwarm(swarm ? T : null, swarm ? st.swarm : 0, t);
  if (dev) {
    $('cBadge').innerHTML = swarm
      ? '群體 <b>' + st.swarm + '</b> 隻\n型別 <b>' + s.alive + '</b>\ndraw call <b>' + s.live + '</b>'
      : (T && T.boss
        /* Boss 沒有變體也沒有精英，讀數要換成「現在是第幾階段」——
           不換的話這一格會一直說「變體 1/3」，那是假情報。 */
        ? '開發模式\n階段 ' + (Math.min(st.stg, (T.stages || 2) - 1) + 1) + '/' + (T.stages || 2)
          + '\nmesh <b>' + modelMeshes() + '</b>'
        : '開發模式\n變體 ' + (st.vi + 1) + '/3' + (st.elite ? ' · 精英' : ''));
  }

  /* 群體壓力時鏡頭要退遠 —— 420 隻鋪開的半徑遠大於單體。 */
  var back = swarm ? 1 + Math.sqrt(st.swarm) * 0.10 : 1;
  var savedDist = FIT.dist;
  FIT.dist = savedDist * back * codexZoom();
  placeCodexCamera(VIEW.yaw, VIEW.pitch);
  FIT.dist = savedDist;
  /* 棚燈跟著鏡頭走 —— **一定要在擺完鏡頭之後**，否則燈用的是上一幀的位置，
     快速拖曳時受光面會落後一格。標尺同理（它投影的是這一幀的鏡頭）。 */
  aimCodexLights(camera.position, FIT.y);
}

/* ---------- 輸入 ---------- */

export function initCodexControls() {
  /* 拖曳綁在 `#screenCodex`：`.screen` 是 `pointer-events:auto`，
     整片蓋在 3D 畫布上，綁 `#stage` 的話事件根本收不到（見 `codex-view.ts`）。 */
  var host = $('screenCodex');
  if (!host || !host.addEventListener) return;   // headless：沒有 DOM 就沒有這個畫面
  bindCodexView(host, codexOpen);
  window.addEventListener('keydown', function (e) {
    if (!codexOpen()) return;
    if (e.key === 'Tab') { toggleCodexList(); syncCodexView(); e.preventDefault(); return; }
    if (e.key === 'ArrowDown') { step(1); e.preventDefault(); }
    if (e.key === 'ArrowUp') { step(-1); e.preventDefault(); }
    if (e.key === 'Escape') closeCodex();
    /* 開發模式的切換鍵。**不做 console 指令**（使用者沒選），
       所以它是圖鑑畫面裡的一個鍵 + 底列的一顆按鈕。 */
    if (e.key === '`' || e.key === 'F2') toggleDev();
  });
  $('btnCodexBack').onclick = function () { closeCodex(); };
  $('btnCodexDev').onclick = function () { toggleDev(); };
  $('btnCList').onclick = function () { toggleCodexList(); syncCodexView(); };
  $('btnCData').onclick = function () { toggleCodexData(); syncCodexView(); };
  var fm = $('cForm');
  if (fm && fm.querySelectorAll) {
    var fbs = fm.querySelectorAll('button');
    for (var f = 0; f < fbs.length; f++) {
      (function (b) {
        b.onclick = function () { setCodexForm(b.getAttribute('data-f') === 'e'); };
      })(fbs[f]);
    }
  }
  initCodexList(function () { renderCodex(); });
  window.addEventListener('resize', function () { applyCodexLayout(); syncCodexView(); });
}

export function toggleDev() {
  dev = !dev;
  if (!dev) { st.swarm = 0; st.die = false; clearCodexSwarm(); }
  renderCodex(); refreshModel();
}

/* ---------- 開關 ---------- */

/** 從哪裡進來的 —— 關閉時要回到同一個地方（開始畫面 or 對局暫停）。 */
var from = 'start';

export function openCodex(origin) {
  from = origin || 'start';
  enterCodexScene();
  resetCodexView();
  resetCodexFilters();
  resetCodexLayout();
  renderCodex();
  refreshModel();
  if (from === 'start') $('screenStart').classList.remove('show');
  $('screenCodex').classList.add('show');
  document.body.classList.add('codex-on');
  /* 面板要先完成佈局才量得到寬度 —— 隔一幀再算視錐偏移。 */
  requestAnimationFrame(syncCodexView);
}

export function closeCodex() {
  $('screenCodex').classList.remove('show');
  document.body.classList.remove('codex-on');
  if (from === 'start') $('screenStart').classList.add('show');
  /* 對局中按 C 進來的：**回到暫停面板**而不是直接回戰場。
     `sim/input.ts` 開圖鑑時順手把 `STATE.paused` 打開了，直接關掉圖鑑的話
     玩家會停在一個「畫面不動、沒有任何指示」的狀態（＝看起來像當機）。 */
  if (from === 'run') openPause();
  camera.clearViewOffset();
  camera.fov = 50; camera.updateProjectionMatrix();
  clearCodexSwarm();
  st.die = false;
  setCodexDie(false);
  setCodexModel(null, {});
  exitCodexScene();
}

export function codexOpen() {
  var el = $('screenCodex');
  return !!el && el.classList.contains('show');
}
export function codexFrom() { return from; }
